import { useState, useEffect, useRef, useMemo, ReactNode, Fragment } from 'react';
import Navbar2 from '../component/navbar2';
import Footer from '../components/BrandFooter';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const BRAND = '#1976d2';
const BRAND_DEEP = '#3171af';
const BRAND_SOFT = '#e1f5fe';
const CREAM = '#FCFDFF';
const PAPER = '#F1F0E4';
const INK = '#17263A';
const INK2 = '#42505F';
const MUTED = '#6E7888';
const LINE = '#E5E8EA';
const GREEN = '#15824E';
const WHITE = '#ffffff';

function FadeIn({ children, delay = 0, className = '' }: { children: ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  const tmr = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(entries => {
      const e = entries[0];
      if (e && e.isIntersecting) { tmr.current = setTimeout(() => setVis(true), delay); obs.disconnect(); }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => { obs.disconnect(); if (tmr.current) clearTimeout(tmr.current); };
  }, [delay]);
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}>
      {children}
    </div>
  );
}

function CheckSvg({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 2 }}>
      <path d="M5 12l5 5L20 7" stroke={GREEN} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      background: BRAND, color: WHITE,
      fontWeight: 700, fontSize: 12.5, letterSpacing: '0.16em',
      textTransform: 'uppercase' as const, padding: '6px 14px', borderRadius: 999
    }}>
      {children}
    </span>
  );
}

const DEFAULT_LADDER = [
  { label: 'Free score', sub: 'See your gaps', price: '$0' },
  { label: 'Living score', sub: 'Track & 80% alerts', price: '$7.99 / mo' },
  { label: 'Capital Assessment', sub: '40-min advisor session', price: '$99 · credited' },
];

const DEFAULT_TIERS = [
  {
    tag: 'Get the basics right',
    name: 'Foundations',
    desc: 'The core building blocks every raise needs, done properly.',
    price: '$2,999',
    features: [
      'Investor-grade financial model',
      'Pitch narrative & core deck',
      'Readiness plan to lift your score',
    ],
    pop: false,
    ctaDark: false,
  },
  {
    tag: 'Get to 80%',
    name: 'Investor-Ready',
    desc: 'Everything needed to walk into the room ready to raise.',
    price: '$6,999',
    features: [
      'Everything in Foundations',
      'Full pitch deck & data room',
      'KPI dashboard & hands-on score lift to 80%',
    ],
    pop: true,
    ctaDark: true,
  },
  {
    tag: 'Be raise-ready',
    name: 'Raise-Ready',
    desc: 'The full programme, plus investor targeting and prep.',
    price: '$11,999',
    features: [
      'Everything in Investor-Ready',
      'Investor targeting & intro prep',
      'Mock pitches & dedicated advisor',
    ],
    pop: false,
    ctaDark: false,
  },
];

type MatrixCell = boolean | string;

const MATRIX: { group: string; rows: { label: string; found: MatrixCell; inv: MatrixCell; raise: MatrixCell }[] }[] = [
  {
    group: 'Strategy & model',
    rows: [
      { label: 'Capital-readiness plan to lift your score', found: true, inv: true, raise: true },
      { label: 'Investor-grade financial model', found: true, inv: true, raise: true },
      { label: 'Business plan & pitch narrative', found: true, inv: true, raise: true },
    ],
  },
  {
    group: 'Materials & data room',
    rows: [
      { label: 'Core pitch deck', found: true, inv: true, raise: true },
      { label: 'Full investor deck', found: false, inv: true, raise: true },
      { label: 'Investor data room setup', found: false, inv: true, raise: true },
      { label: 'KPI dashboard', found: false, inv: true, raise: true },
      { label: 'Hands-on score lift to 80%', found: false, inv: true, raise: true },
    ],
  },
  {
    group: 'Raise preparation',
    rows: [
      { label: 'Investor targeting & intro prep', found: false, inv: false, raise: true },
      { label: 'Mock pitches & negotiation prep', found: false, inv: false, raise: true },
      { label: 'Advisor support', found: '1 session', inv: 'Ongoing', raise: 'Dedicated' },
    ],
  },
];

const DEFAULT_FAQS = [
  { q: 'Is the readiness score really free?', a: 'Yes. Your static readiness score and dashboard are free, so you can see exactly where you stand before spending anything.' },
  { q: 'What do I get for $7.99 a month?', a: 'The living score: your readiness score tracked over time, with movement nudges and an alert when you cross the 80% investor-ready line.' },
  { q: "What's included in the $99 Capital Assessment?", a: 'A focused 40-minute session with an advisor who reviews your readiness and maps your gaps. The $99 is credited against any package you take afterwards.' },
  { q: 'Can I buy just one package?', a: 'Yes — each package stands on its own. Most founders start with Foundations or Investor-Ready and add more only if they need it.' },
  { q: "Do you guarantee I'll raise or get funded?", a: "No — no one credibly can. We maximise your readiness and the strength of your materials. Full raise execution is rolling out, so ask us about timing for your raise." },
  { q: 'Which markets do you cover?', a: 'We work with founders across MENA, the UK and the US.' },
];

function MatrixCell({ val, isInv }: { val: MatrixCell; isInv: boolean }) {
  const bg = isInv ? '#EDF6FF' : undefined;
  const cls = isInv ? 'cp-pop-cell' : undefined;
  if (typeof val === 'string') {
    return (
      <td className={cls} style={{ textAlign: 'center', fontWeight: 700, fontSize: 13.5, color: INK, padding: '15px 20px', borderBottom: `1px solid ${LINE}`, background: bg }}>
        {val}
      </td>
    );
  }
  return (
    <td className={cls} style={{ textAlign: 'center', padding: '15px 20px', borderBottom: `1px solid ${LINE}`, background: bg }}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {val ? <CheckSvg size={19} /> : <span style={{ color: MUTED }}>–</span>}
      </div>
    </td>
  );
}

export default function ConsultingPricing() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const isLoggedIn = useMemo(() => {
    try {
      return !!(
        localStorage.getItem('user') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('user') ||
        sessionStorage.getItem('token')
      );
    } catch { return false; }
  }, []);

  const ctaPath = isLoggedIn ? '/dashboard' : '/consulting/signup';

  // Live, admin-editable pricing (Firestore consultingPricing/current); falls back to defaults.
  const [ladder, setLadder] = useState<any[]>(DEFAULT_LADDER);
  const [tiers, setTiers] = useState<any[]>(DEFAULT_TIERS);
  const [faqs, setFaqs] = useState<any[]>(DEFAULT_FAQS);
  useEffect(() => {
    const ref = doc(db, 'consultingPricing', 'current');
    return onSnapshot(ref, snap => {
      if (!snap.exists()) return;
      const d = snap.data();
      if (Array.isArray(d.ladder) && d.ladder.length) setLadder(d.ladder);
      if (Array.isArray(d.tiers) && d.tiers.length) setTiers(d.tiers);
      if (Array.isArray(d.faqs) && d.faqs.length) setFaqs(d.faqs);
    }, err => { if (err.name !== 'AbortError') console.warn('consulting pricing snapshot:', err); });
  }, []);

  const tdBase: React.CSSProperties = { padding: '15px 20px', borderBottom: `1px solid ${LINE}` };
  const btnGhost: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '8px 16px', borderRadius: 999, fontWeight: 700, fontSize: 13, border: `2px solid ${INK}`, marginTop: 12, textDecoration: 'none', background: 'transparent', color: INK, transition: 'background .2s, color .2s' };

  return (
    <div style={{ fontFamily: "'Montserrat', system-ui, sans-serif", color: INK, background: CREAM, overflowX: 'clip' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');
        .cp-tier { transition: transform .25s cubic-bezier(.2,.7,.2,1), box-shadow .25s; }
        .cp-tier:hover { transform: translateY(-6px); box-shadow: 0 26px 60px -24px rgba(23,38,58,.30); }
        .cp-btn-dark:hover { transform: translateY(-3px); box-shadow: 0 16px 30px -12px rgba(0,0,0,.55); }
        .cp-btn-brand:hover { transform: translateY(-3px); box-shadow: 0 16px 30px -12px rgba(25,118,210,.55); }
        .cp-btn-ghost:hover { background: ${INK} !important; color: ${CREAM} !important; }
        .cp-matrix-row:hover td { background: rgba(23,38,58,.022); }
        .cp-matrix-row:hover td.cp-pop-cell { background: #D6E8FF; }
        .cp-rung { transition: transform .2s, box-shadow .2s; }
        .cp-rung:hover { transform: translateY(-3px); box-shadow: 0 12px 30px -12px rgba(23,38,58,.22); }
        .cp-footer-link:hover { color: ${BRAND} !important; }
      `}</style>

      <Navbar2 signupTo="/consulting/signup" />

      {/* Chain divider */}

      {/* Hero */}
      <header style={{ position: 'relative', background: `radial-gradient(120% 90% at 80% -10%, ${BRAND_SOFT} 0%, ${CREAM} 55%)`, overflow: 'hidden', padding: '60px 0 22px' }}>
        <div style={{ position: 'absolute', borderRadius: '50%', filter: 'blur(10px)', width: 380, height: 380, background: BRAND, top: -120, right: -80, opacity: 0.35, zIndex: 0 }} />
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 24px', textAlign: 'center', position: 'relative', zIndex: 2 }}>
          <FadeIn>
            <Eyebrow>Pricing · USD</Eyebrow>
          </FadeIn>
          <FadeIn delay={80}>
            <h1 style={{ fontSize: 'clamp(42px, 5.6vw, 68px)', fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1.05, marginTop: 18, color: INK }}>
              Start free.{' '}
              <span style={{ position: 'relative', whiteSpace: 'nowrap' }}>
                Pay as you get serious.
                <span style={{ position: 'absolute', left: -2, right: -2, bottom: 6, height: '0.32em', background: BRAND_SOFT, zIndex: -1, transform: 'skewX(-4deg)', display: 'block' }} />
              </span>
            </h1>
          </FadeIn>
          <FadeIn delay={160}>
            <p style={{ fontSize: 20, color: INK2, maxWidth: 580, margin: '14px auto 0', lineHeight: 1.55 }}>
              Begin with a free readiness score and go deeper when you're ready to raise. Your $99 Capital Assessment is credited against any package.
            </p>
          </FadeIn>
        </div>
      </header>

      {/* Plans */}
      <section id="plans" style={{ padding: '40px 0 80px' }}>
        <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 24px' }}>

          {/* Ladder */}
          <FadeIn>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', marginBottom: 26 }}>
              {ladder.map(r => (
                <div key={r.label} className="cp-rung" style={{ background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 16, padding: '16px 20px', textAlign: 'center', minWidth: 150 }}>
                  <b style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 18, display: 'block', color: INK }}>{r.label}</b>
                  <small style={{ color: MUTED, fontSize: 12.5, display: 'block', marginTop: 2 }}>{r.sub}</small>
                  <div style={{ fontWeight: 700, fontSize: 14, marginTop: 6, color: INK }}>{r.price}</div>
                </div>
              ))}
            </div>
          </FadeIn>

          {/* Tier cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, alignItems: 'stretch' }}>
            {tiers.map((tier, i) => (
              <FadeIn key={tier.name} delay={i * 80}>
                <div className="cp-tier" style={{
                  background: WHITE,
                  border: `1.5px solid ${tier.pop ? INK : LINE}`,
                  boxShadow: tier.pop ? '0 26px 60px -24px rgba(23,38,58,.30)' : undefined,
                  borderRadius: 22, padding: 30,
                  display: 'flex', flexDirection: 'column',
                  position: 'relative', height: '100%',
                }}>
                  {tier.pop && (
                    <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: BRAND, color: WHITE, border: `2px solid ${BRAND}`, fontSize: 12, fontWeight: 700, padding: '4px 14px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                      Most popular
                    </div>
                  )}
                  <span style={{ fontSize: 13, fontWeight: 700, color: MUTED }}>{tier.tag}</span>
                  <h3 style={{ fontSize: 23, fontWeight: 800, marginTop: 6, marginBottom: 4 }}>{tier.name}</h3>
                  <p style={{ fontSize: 13.5, color: MUTED, minHeight: 42, lineHeight: 1.5 }}>{tier.desc}</p>
                  <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 40, fontWeight: 800, marginTop: 14, color: INK }}>{tier.price}</div>
                  <ul style={{ listStyle: 'none', margin: '18px 0 24px', display: 'flex', flexDirection: 'column', gap: 11, padding: 0 }}>
                    {tier.features.map(f => (
                      <li key={f} style={{ display: 'flex', gap: 10, fontSize: 14.5, alignItems: 'flex-start' }}>
                        <CheckSvg /><span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={ctaPath} className={tier.ctaDark ? 'cp-btn-dark' : 'cp-btn-ghost'} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
                    padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15,
                    border: `2px solid ${INK}`,
                    background: tier.ctaDark ? INK : 'transparent',
                    color: tier.ctaDark ? CREAM : INK,
                    textDecoration: 'none', marginTop: 'auto',
                    transition: 'transform .16s, box-shadow .22s, background .2s, color .2s',
                  }}>
                    Choose {tier.name}
                  </Link>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={80}>
            <p style={{ fontSize: 13, color: MUTED, marginTop: 16, textAlign: 'center' }}>
              Your $99 Capital Assessment is credited against any package. Prices in USD.
            </p>
          </FadeIn>

          {/* Keystone banner */}
          <FadeIn delay={120}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', background: WHITE, border: `1px solid ${LINE}`, borderLeft: `4px solid ${BRAND}`, borderRadius: 14, padding: '16px 18px', marginTop: 18, boxShadow: '0 8px 24px -14px rgba(23,38,58,.22)' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 2 }}>
                <circle cx="12" cy="12" r="9" stroke={BRAND} strokeWidth="2.2" fill="none" />
                <path d="M8 12l2.5 2.5L16 9" stroke={BRAND} strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <b style={{ color: INK }}>80% is the investor-ready line.</b>
                <p style={{ margin: '2px 0 0', color: INK2, fontSize: 14 }}>
                  Investor-Ready and Raise-Ready are built to get your live score to 80% — the point most investors expect before a serious conversation.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Compare matrix */}
      <section id="compare" style={{ padding: '80px 0', background: PAPER }}>
        <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 24px' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <Eyebrow>Compare</Eyebrow>
              <h2 style={{ fontSize: 'clamp(31px, 4.2vw, 50px)', fontWeight: 800, letterSpacing: '-0.022em', margin: '18px 0 14px', color: INK }}>
                What's in each package
              </h2>
              <p style={{ fontSize: 19, color: INK2, maxWidth: 640, margin: '0 auto' }}>
                Each package builds on the last — from core materials to a full, raise-ready programme.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={80}>
            <div style={{ overflowX: 'auto', borderRadius: 20, border: `1px solid ${LINE}`, boxShadow: '0 26px 60px -24px rgba(23,38,58,.30)', background: WHITE }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14.5, minWidth: 720 }}>
                <thead>
                  <tr>
                    <th style={{ ...tdBase, background: PAPER, textAlign: 'left', fontSize: 12, color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', width: '34%' }}>
                      Compare plans
                    </th>
                    <th style={{ ...tdBase, background: PAPER, textAlign: 'center', fontWeight: 800, fontSize: 15.5 }}>
                      Foundations
                      <span style={{ display: 'block', color: MUTED, fontWeight: 700, fontSize: 13, marginTop: 3 }}>$2,999</span>
                      <Link to={ctaPath} className="cp-btn-ghost" style={btnGhost}>Get started</Link>
                    </th>
                    <th style={{ ...tdBase, background: BRAND, color: WHITE, textAlign: 'center', fontWeight: 800, fontSize: 15.5, borderBottom: `1px solid ${BRAND}` }}>
                      <span style={{ display: 'inline-block', marginBottom: 7, fontSize: 10, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: BRAND, background: WHITE, padding: '3px 9px', borderRadius: 999 }}>Most popular</span>
                      <br />Investor-Ready
                      <span style={{ display: 'block', color: 'rgba(255,255,255,0.88)', fontWeight: 700, fontSize: 13, marginTop: 3 }}>$6,999</span>
                      <Link to={ctaPath} className="cp-btn-dark" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '8px 16px', borderRadius: 999, fontWeight: 700, fontSize: 13, border: `2px solid ${WHITE}`, marginTop: 12, textDecoration: 'none', background: WHITE, color: INK, transition: 'transform .16s, box-shadow .22s' }}>
                        Get started
                      </Link>
                    </th>
                    <th style={{ ...tdBase, background: PAPER, textAlign: 'center', fontWeight: 800, fontSize: 15.5 }}>
                      Raise-Ready
                      <span style={{ display: 'block', color: MUTED, fontWeight: 700, fontSize: 13, marginTop: 3 }}>$11,999</span>
                      <Link to={ctaPath} className="cp-btn-ghost" style={btnGhost}>Get started</Link>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Typical timeline */}
                  <tr className="cp-matrix-row">
                    <td style={{ ...tdBase, fontWeight: 600, color: INK2 }}>Typical timeline</td>
                    <td style={{ ...tdBase, textAlign: 'center', fontWeight: 700, fontSize: 13.5, color: INK }}>2–3 weeks</td>
                    <td className="cp-pop-cell" style={{ ...tdBase, textAlign: 'center', fontWeight: 700, fontSize: 13.5, color: INK, background: '#EDF6FF' }}>4–6 weeks</td>
                    <td style={{ ...tdBase, textAlign: 'center', fontWeight: 700, fontSize: 13.5, color: INK }}>6–12 weeks</td>
                  </tr>

                  {MATRIX.map(group => (
                    <Fragment key={group.group}>
                      <tr>
                        <td colSpan={4} style={{ background: BRAND_SOFT, color: INK, fontWeight: 800, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.07em', padding: '10px 20px', borderBottom: `1px solid ${LINE}` }}>
                          {group.group}
                        </td>
                      </tr>
                      {group.rows.map(row => (
                        <tr key={row.label} className="cp-matrix-row">
                          <td style={{ ...tdBase, fontWeight: 600, color: INK2 }}>{row.label}</td>
                          <MatrixCell val={row.found} isInv={false} />
                          <MatrixCell val={row.inv} isInv={true} />
                          <MatrixCell val={row.raise} isInv={false} />
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Raise bar */}
      <section style={{ padding: '80px 0' }}>
        <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 24px' }}>
          <FadeIn>
            <div style={{ background: INK, color: CREAM, borderRadius: 22, padding: '34px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ color: CREAM, fontSize: 23, fontWeight: 800 }}>Planning a raise?</h3>
                <p style={{ color: '#AEB9D6', fontSize: 14.5, marginTop: 4, maxWidth: 520 }}>
                  Our advisors get you raise-ready — model, pitch, data room and investor prep. Full raise execution (including success-fee engagements) is rolling out, so talk to us about timing.
                </p>
              </div>
              <Link to={ctaPath} className="cp-btn-brand" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, border: `2px solid ${BRAND}`, background: BRAND, color: WHITE, textDecoration: 'none', whiteSpace: 'nowrap', transition: 'transform .16s, box-shadow .22s' }}>
                Talk about your raise →
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" style={{ padding: '80px 0', background: PAPER }}>
        <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 24px' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 22 }}>
              <Eyebrow>Pricing FAQ</Eyebrow>
              <h2 style={{ fontSize: 'clamp(31px, 4.2vw, 50px)', fontWeight: 800, letterSpacing: '-0.022em', margin: '18px 0 0', color: INK }}>
                Questions about pricing
              </h2>
            </div>
          </FadeIn>
          <FadeIn delay={80}>
            <div style={{ maxWidth: 780, margin: '22px auto 0' }}>
              {faqs.map((faq, i) => (
                <div key={i} style={{ borderBottom: `1px solid ${LINE}` }}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left', padding: '22px 4px', fontSize: 18, fontFamily: "'Montserrat', sans-serif", fontWeight: 600, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, color: INK }}
                  >
                    {faq.q}
                    <span style={{ fontSize: 25, color: BRAND_DEEP, flexShrink: 0, transition: 'transform .3s', display: 'inline-block', transform: openFaq === i ? 'rotate(45deg)' : 'none' }}>+</span>
                  </button>
                  <div style={{ maxHeight: openFaq === i ? 280 : 0, overflow: 'hidden', transition: 'max-height .32s ease', color: INK2, fontSize: 15.5 }}>
                    <p style={{ padding: '0 4px 22px' }}>{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ padding: '80px 0' }}>
        <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 24px' }}>
          <FadeIn>
            <div style={{ background: BRAND, borderRadius: 30, padding: '70px 40px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(${INK} 0.9px, transparent 0.9px)`, backgroundSize: '24px 24px', opacity: 0.07 }} />
              <h2 style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', fontWeight: 900, color: WHITE, position: 'relative', letterSpacing: '-0.022em' }}>
                See where you stand — free.
              </h2>
              <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.92)', maxWidth: 560, margin: '16px auto 28px', position: 'relative' }}>
                Get your readiness score in minutes, then book a Capital Assessment when you're ready to close the gap and raise.
              </p>
              <Link to={ctaPath} className="cp-btn-dark" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '16px 32px', borderRadius: 999, fontWeight: 700, fontSize: 16, border: `2px solid ${INK}`, background: INK, color: CREAM, textDecoration: 'none', position: 'relative', transition: 'transform .16s, box-shadow .22s' }}>
                Get your free readiness score →
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
